import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  parseJSONWithBetterErrors,
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {useMutation} from '@github-ui/react-query'
import {useMarkersActions} from '../stores/selectors'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import type {FilesRoutePayload} from '../page-data/payloads/files'
import type {FileKey, LineKey} from '@github-ui/diff-lines/types'

export type UpdateReviewCommentMutationResponse = {
  body: string
  bodyHTML: string
  bodyVersion: string
  commentDatabaseId: number
  threadId: number
}

/**
 * Updates the body of a review comment
 */
export function useUpdateReviewCommentMutation({
  filePath,
  lineMarkersKey,
  onSuccess,
}: {
  filePath: string
  lineMarkersKey: LineKey | FileKey
  onSuccess?: () => void
}) {
  const {
    pullRequest: {pathName: basePath},
  } = useRoutePayload<FilesRoutePayload>()
  const apiUrl = `${basePath}/page_data/${PageData.updateReviewComment}`
  const {updateThreadComment} = useMarkersActions()

  return useMutation({
    mutationFn: async ({commentId, bodyVersion, body}: {commentId: string; bodyVersion?: string; body: string}) => {
      const response = await fetchWithErrorHandling(`${apiUrl}${bodyVersion ? `?body_version=${bodyVersion}` : ''}`, {
        method: 'PUT',
        body: {
          body,
          commentId,
        },
      })

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
      return json
    },
    onSuccess: (data: UpdateReviewCommentMutationResponse) => {
      updateThreadComment({
        threadID: data.threadId,
        filePath,
        lineMarkersKey,
        match: storedComment => storedComment.databaseId === data.commentDatabaseId,
        transform: oldComment => {
          if (oldComment) {
            return {
              ...oldComment,
              body: data.body,
              bodyHTML: data.bodyHTML,
              bodyVersion: data.bodyVersion,
            }
          }
        },
      })

      onSuccess?.()
    },
  })
}
